import { useEffect, useRef, useState } from 'react'
import { cn } from '#app/utils/misc.tsx'
import {
	getMuxHlsUrl,
	getMuxPosterUrl,
	useDeferredVideoMount,
} from './deferred-video-layer.tsx'

interface HoverVideoOverlayProps {
	muxPlaybackId: string
	className?: string
}

/**
 * Video overlay that plays on hover, sitting absolutely over a parent
 * with `group` class. The underlying <Img> at the call site is the LCP
 * candidate; the <video> element only mounts after window.load + viewport
 * intersection so it never competes for bandwidth.
 *
 * Hidden on touch devices (no hover events).
 */
export function HoverVideoOverlay({
	muxPlaybackId,
	className,
}: HoverVideoOverlayProps) {
	const { hostRef, shouldMount } =
		useDeferredVideoMount<HTMLDivElement>()
	const videoRef = useRef<HTMLVideoElement | null>(null)
	const [isPlaying, setIsPlaying] = useState(false)

	useEffect(() => {
		const host = hostRef.current
		if (!host) return
		const parent = host.closest('.group') ?? host.parentElement
		if (!parent) return
		const onEnter = () => {
			const video = videoRef.current
			if (video) void video.play().catch(() => {})
		}
		const onLeave = () => {
			const video = videoRef.current
			if (!video) return
			video.pause()
			video.currentTime = 0
		}
		parent.addEventListener('mouseenter', onEnter)
		parent.addEventListener('mouseleave', onLeave)
		return () => {
			parent.removeEventListener('mouseenter', onEnter)
			parent.removeEventListener('mouseleave', onLeave)
		}
	}, [hostRef])

	return (
		<div
			ref={hostRef}
			className={cn(
				'pointer-events-none absolute inset-0 hidden md:block',
				className,
			)}
		>
			{shouldMount ? (
				<video
					ref={videoRef}
					src={getMuxHlsUrl(muxPlaybackId)}
					poster={getMuxPosterUrl(muxPlaybackId, 800)}
					muted
					loop
					playsInline
					preload="metadata"
					onPlaying={() => setIsPlaying(true)}
					onPause={() => setIsPlaying(false)}
					className={cn(
						'h-full w-full object-cover transition-opacity duration-300',
						isPlaying ? 'opacity-100' : 'opacity-0',
						'group-hover:opacity-100',
					)}
				/>
			) : null}
		</div>
	)
}
